import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, SyntheticEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../components/shared/useUser';
import { Franchise } from '../../../../app/services/api/generated';
import { getUserReportClient, UserReportFilterDto } from '../../../../app/services/api/users/userReportClient';
import dayjs from 'dayjs';

type IProps = {
  onClose?: () => void;
}

const UserReportForm = (props: IProps) => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation('common');

  const franchises = (user?.franchises ?? []).filter(f =>
    user?.stores?.some(s => s.franchiseId === f.franchiseId),
  );

  const [selectedFranchises, setSelectedFranchises] = useState<Franchise[]>(
    franchises.length === 1 ? [franchises[0]] : [],
  );
  const [checkedAllFranchises, setCheckedAllFranchises] = useState<boolean>(franchises.length === 1);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);

  const getMaxDate = () => dayjs().format('YYYY-MM-DD');
  const getMinDate = () => new Date('1900-01-01').toISOString().split('T')[0];

  const handleFranchiseChange = (_event: SyntheticEvent, value: Franchise[]) => {
    setSelectedFranchises(value);
    setCheckedAllFranchises(value.length === franchises.length);
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCheckedAllFranchises(checked);
    setSelectedFranchises(checked ? [...franchises] : []);
  };

  const handleFromDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const effectiveFranchises = checkedAllFranchises ? franchises : selectedFranchises;
    if (effectiveFranchises.length === 0) return;

    setIsGenerating(true);
    setErrorMessage('');

    const filter: UserReportFilterDto = {
      startDate: new Date(fromDate + 'T00:00:00Z').toISOString(),
      endDate: new Date(toDate + 'T23:59:59.999Z').toISOString(),
      franchiseIds: effectiveFranchises.map(f => f.franchiseId as number),
    };

    getUserReportClient()
      .runUserReport(filter)
      .then(() => {
        setIsRequestSubmitted(true);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : t('reporting.user-report.error');
        setErrorMessage(message);
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={3}
              options={franchises}
              value={selectedFranchises}
              onChange={handleFranchiseChange}
              disabled={checkedAllFranchises}
              getOptionKey={(option) => option.franchiseId ?? ''}
              getOptionLabel={(option) => option.franchiseName ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={selectedFranchises.length === 0 && !checkedAllFranchises}
                  label={t('reporting.user-report.select-franchise')}
                  placeholder={t('reporting.user-report.select-franchise')}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllFranchises}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-franchises')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.start-date')}
            type="date"
            required
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: getMinDate(), max: toDate !== '' ? toDate : getMaxDate() } }}
            fullWidth
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <TextField
            label={t('reporting.end-date')}
            type="date"
            required
            value={toDate}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            InputProps={{ inputProps: { min: fromDate !== '' ? fromDate : getMinDate(), max: getMaxDate() } }}
            fullWidth
          />
        </Grid>
        {errorMessage && (
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        )}
        {isRequestSubmitted && (
          <Grid item
            xs={12}>
            <Alert severity="success">{t('reporting.generic.post-generation-message')}</Alert>
          </Grid>
        )}
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={isGenerating}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default UserReportForm;